import type { ReactNode } from 'react';

export function PageHead({
  title,
  subtitle,
  counter,
  actions,
}: {
  title: string;
  subtitle?: string;
  counter?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="ts-flex-between" style={{ marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 className="ts-page-title">{title}</h1>
        {subtitle && <p className="ts-page-sub">{subtitle}</p>}
      </div>
      <div className="ts-flex-row" style={{ gap: 10 }}>
        {counter}
        {actions}
      </div>
    </div>
  );
}

export function SectionCard({
  icon,
  title,
  hint,
  children,
}: {
  icon: string;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="ts-card">
      <div className="if-card-head">
        <i className={`bi ${icon}`} />
        <span>{title}</span>
        {hint && <span style={{ color: 'var(--ts-muted)', fontWeight: 400, marginLeft: 'auto' }}>{hint}</span>}
      </div>
      <div className="if-card-body">{children}</div>
    </div>
  );
}

export function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="ts-label">
        {label} {required && <span style={{ color: 'var(--ts-danger)' }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: '0.72rem', color: 'var(--ts-muted)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

export function StatusBadge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="ts-badge"
      style={{ background: `color-mix(in srgb, ${color} 18%, transparent)`, color, borderColor: color }}
    >
      {label}
    </span>
  );
}

export function Drawer({
  title,
  open,
  onClose,
  footer,
  children,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="cg-drawer-backdrop" onClick={onClose}>
      <div className="cg-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cg-drawer-head">
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h2>
          <button className="ts-btn ts-btn-ghost" onClick={onClose} aria-label="Хаах">
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <div className="cg-drawer-body">{children}</div>
        {footer && <div className="cg-drawer-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ icon = 'bi-inbox', text }: { icon?: string; text: string }) {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ts-muted)' }}>
      <i className={`bi ${icon}`} style={{ fontSize: '2rem', display: 'block', marginBottom: 8 }} />
      {text}
    </div>
  );
}

export interface Col<T> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: number;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  cols,
  rows,
  onRowClick,
  empty = 'Бичлэг алга.',
}: {
  cols: Col<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  empty?: string;
}) {
  if (!rows.length) return <EmptyState text={empty} />;
  return (
    <div className="ts-card" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
      <table className="ct-table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} style={{ textAlign: c.align ?? 'left', width: c.width }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} onClick={() => onRowClick?.(row)}>
              {cols.map((c) => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
